// Formats that can hold several frames, mapped to the type ImageDecoder reads them as.
const DECODER_TYPES: Record<string, string> = {
  "image/gif": "image/gif",
  "image/apng": "image/png",
  "image/png": "image/png",
  "image/webp": "image/webp",
}

// Resolves once the header is parsed; the pixels decode lazily on the first drawImage.
const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error("The browser couldn't read the image"))
    image.src = src
  })

const readAsDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error("The browser couldn't read the file"))
    reader.readAsDataURL(blob)
  })

// An image file the visitor picked, read in the browser.
export class ImageFile {
  static readonly TYPES_BY_EXTENSION: Record<string, string> = {
    png: "image/png",
    apng: "image/apng",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    gif: "image/gif",
    svg: "image/svg+xml",
    bmp: "image/bmp",
  }

  static sniffType(file: File) {
    if (file.type && file.type !== "application/octet-stream") return file.type

    const extension = file.name.split(".").pop()?.toLowerCase() ?? ""
    return ImageFile.TYPES_BY_EXTENSION[extension] ?? ""
  }

  // The type ImageDecoder reads an animated format as; undefined for a still-only format.
  static decoderTypeFor(type: string): string | undefined {
    return DECODER_TYPES[type]
  }

  // Never inserted into the document. SVGs go through a data: URL because a blob: URL of a
  // user SVG, opened in a new tab, would run its scripts on our origin.
  static async decode(file: File, type: string) {
    if (type === "image/svg+xml") {
      return loadImage(await readAsDataUrl(new Blob([file], { type })))
    }

    const url = URL.createObjectURL(file)
    try {
      return await loadImage(url)
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  // Read from the file's own bytes, for a browser whose ImageDecoder can't count the frames.
  static async holdsAnimation(file: File, type: string) {
    const decoderType = ImageFile.decoderTypeFor(type)
    if (!decoderType) return false
    if (decoderType === "image/gif") return true

    const bytes = new Uint8Array(await file.arrayBuffer())
    const text = new TextDecoder("latin1").decode(bytes)
    // WebP flags an animation in its VP8X header; a PNG has an acTL chunk ahead of its image data.
    if (decoderType === "image/webp") return text.startsWith("VP8X", 12) && (bytes[20] & 2) !== 0

    return text.slice(0, text.indexOf("IDAT")).includes("acTL")
  }
}
